import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { TILE_SIZE } from './constants';

/**
 * Between the top of what a label stands over and the label's lower edge, as the eye sees them:
 * enough to clear the far side of a head, which stands higher than its top point when the eye
 * looks down on it.
 */
export const OVERHEAD_GAP = TILE_SIZE * 0.12;

/**
 * How what hangs over a piece grows with the eye's distance: its size in the headset's seat
 * (0.9 m) and nearer, up to 1.8 times it from the desktop's framing (1.7 m), where a sign was about
 * seven pixels tall (client-board:quality#12). The signs and the odds share it, so an odds label
 * stays clear of the sign under it; at 1.8 times the odds on two squares side by side still fit.
 */
export const OVERHEAD_GROW = { reference: 0.9, max: 1.8 };

const UP = Vector3.Up();

/**
 * The axes of a label at `at` that faces the eye with no roll: `right` and `up` lie in its plane,
 * `forward` runs from the eye to it. From straight above, where no roll is defined, right is +X.
 *
 * A label is lifted along this `up`, not straight up, over what it names. Lifted straight up, a
 * label seen from high above slid down over the head it named, its lower half hanging towards
 * the eye; along `up` its lower edge clears the head by the same gap from any height.
 */
export function eyeAxes(
  at: Vector3,
  eye: Vector3,
  right: Vector3,
  up: Vector3,
  forward: Vector3 = new Vector3(),
): void {
  at.subtractToRef(eye, forward);
  if (forward.lengthSquared() < 1e-12) forward.set(0, 0, 1);
  forward.normalize();
  Vector3.CrossToRef(UP, forward, right);
  if (right.lengthSquared() < 1e-8) right.set(1, 0, 0);
  right.normalize();
  Vector3.CrossToRef(forward, right, up);
}
